import { AnalyticsEvent } from '../Model/analytics.event';
import { ButtonClickEventContext } from '../Model/Events/UserJourney/button-click.event';

/**
 * Service responsible for tracking and logging various analytics events within the application.
 * This handles different types of events, including navigation, application launches, user logins,
 * errors, performance metrics, and API-related issues.
 */
export abstract class AnalyticsService {
    /**
     * Logs a button click event.
     *
     * @param buttonId - The unique identifier of the clicked button
     * @param form - The form or screen ID
     * @param context - Optional contextual information about where the button was clicked
     * @returns A promise that resolves when the event has been logged
     */
    abstract logButtonClick(buttonId: string, form: string, context?: ButtonClickEventContext): Promise<void>;

    /**
     * Logs a navigation event when a user navigates from one page to another.
     *
     * @param from - The source page or route the user navigated from
     * @param to - The destination page or route the user navigated to
     * @returns A promise that resolves when the event has been logged
     */
    abstract logNavigation(from: string, to: string): Promise<void>;

    /**
     * Logs an application launch event when the application starts.
     *
     * @returns A promise that resolves when the event has been logged
     */
    abstract logAppLaunch(): Promise<void>;

    /**
     * Logs a user login event when a user successfully authenticates.
     *
     * @param method - The authentication method used for login (e.g., 'email', 'google', 'facebook')
     * @returns A promise that resolves when the event has been logged
     */
    abstract logUserLogin(method: string): Promise<void>;

    /**
     * Logs an error event when an error occurs in the application.
     *
     * @param errorType - The type or category of the error
     * @param errorMessage - The error message or description
     * @returns A promise that resolves when the event has been logged
     */
    abstract logError(errorType: string, errorMessage: string): Promise<void>;

    /**
     * Logs a performance event to track the duration of an action.
     *
     * @param action - The name of the action being measured
     * @param startTime - The timestamp when the action started (in milliseconds)
     * @param success - Whether the action completed successfully, defaults to true
     * @returns A promise that resolves when the event has been logged
     */
    abstract logPerformance(action: string, startTime: number, success: boolean): Promise<void>;

    /**
     * Logs an API error event when an API request fails.
     *
     * @param endpoint - The API endpoint that was called
     * @param statusCode - The HTTP status code returned by the API
     * @param request - The request data that was sent to the API
     * @param response - The response data that was received from the API
     * @returns A promise that resolves when the event has been logged
     */
    abstract logApiError(
        endpoint: string,
        statusCode: number,
        request: Record<string, unknown>,
        response: Record<string, unknown>
    ): Promise<void>;

    /**
     * Logs a custom event with the provided analytics event data.
     *
     * @param {AnalyticsEvent} event - The analytics event containing details about the custom event to be logged.
     * @return {Promise<void>} A promise that resolves when the custom event has been successfully logged.
     */
    abstract logCustomEvent(event: AnalyticsEvent): Promise<void>;
}
